/**
 * Дебаунсер сверки с сервером (план 3b, задача 4).
 *
 * До него каждая успешная мутация и каждый слив очереди дёргали полный
 * `GET /api/bootstrap`: раскладка остатка по пяти целям стоила пять полных
 * выборок операций. Оптимистичное состояние показывает результат сразу, поэтому
 * сверка нужна не «сейчас же», а «не позже чем через окно».
 *
 * Семантика — leading + trailing: первый запрос после паузы выполняется
 * немедленно (возврат в сеть должен подтягивать данные без задержки), а пачка
 * запросов внутри окна сводится ровно к одному хвостовому вызову. Хвост
 * гарантирован: запрос никогда не теряется, максимум откладывается на окно.
 */
export interface RefreshScheduler {
  /** Попросить сверку. Немедленно либо хвостовым вызовом — не чаще раза в окно. */
  request(): void;
  /** Снять отложенный вызов (размонтирование провайдера). */
  cancel(): void;
}

export function createRefreshScheduler(
  run: () => void,
  minIntervalMs: number,
  now: () => number = () => Date.now(),
): RefreshScheduler {
  // -Infinity: первый request() всегда попадает в «пауза больше окна»
  let lastRunAt = -Infinity;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function fire(): void {
    lastRunAt = now();
    run();
  }

  return {
    request() {
      if (timer !== null) return; // хвост уже запланирован — коалесцируем в него
      const wait = minIntervalMs - (now() - lastRunAt);
      if (wait <= 0) {
        fire();
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        fire();
      }, wait);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
